import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Target, Calculator, ArrowLeft } from "lucide-react";
import { Button } from "@/common/components/ui/button";
import { Card, CardContent } from "@/common/components/ui/card";
import { CompactCalculatorRow } from "../CompactCalculatorRow";
import { calculateNPS, formatCurrency } from "../../utils/calculations";
import ProductLayout from "@/common/components/ProductLayout";

export const NPSCalculator = () => {
  const navigate = useNavigate();
  const [npsMonthly, setNpsMonthly] = useState(5000);
  const [npsAge, setNpsAge] = useState(30);
  const [npsReturn, setNpsReturn] = useState(10);
  const retirementAge = 60;

  const npsResult = useMemo(
    () => calculateNPS(npsMonthly, npsAge, npsReturn, retirementAge),
    [npsMonthly, npsAge, npsReturn]
  );

  return (
    <ProductLayout>
      <div className="min-h-screen bg-background py-8">
        <div className="container mx-auto px-4 md:px-6">
          <div className="mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate("/calculators")}
              className="mb-4 pl-0 hover:bg-transparent hover:text-primary"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Calculators
            </Button>
            <div className="flex items-center gap-4">
              <div className="rounded-lg bg-primary/10 p-3 text-primary">
                <Calculator className="h-8 w-8" />
              </div>
              <h1 className="text-3xl font-bold text-secondary">
                NPS Calculator
              </h1>
            </div>
          </div>

          <div className="mx-auto max-w-7xl">
            <div className="grid gap-8 lg:grid-cols-2">
              <Card className="border-border bg-card">
                <CardContent className="space-y-6 p-6 md:p-8">
                  <div className="mb-2 flex items-center gap-3">
                    <div className="rounded-xl bg-cyan-100 p-3">
                      <Target className="h-6 w-6 text-cyan-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-secondary">
                        NPS Calculator
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        National Pension Scheme
                      </p>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <CompactCalculatorRow
                      label="Monthly Investment"
                      value={npsMonthly}
                      onChange={setNpsMonthly}
                      min={500}
                      max={1000000}
                      step={500}
                      prefix="₹"
                      colorClass="text-primary"
                    />

                    <CompactCalculatorRow
                      label="Current Age"
                      value={npsAge}
                      onChange={setNpsAge}
                      min={18}
                      max={60}
                      step={1}
                      suffix="Yrs"
                      colorClass="text-blue-600"
                    />

                    <CompactCalculatorRow
                      label="Expected return rate (p.a)"
                      value={npsReturn}
                      onChange={setNpsReturn}
                      min={1}
                      max={30}
                      step={0.1}
                      suffix="%"
                      colorClass="text-green-600"
                    />
                  </div>
                </CardContent>
              </Card>

              <div className="space-y-6">
                <Card className="border-border bg-muted/20">
                  <CardContent className="p-8">
                    <div className="mb-8 text-center">
                      <p className="mb-1 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                        Projected Retirement Corpus
                      </p>
                      <h2 className="text-4xl font-black text-cyan-700">
                        {formatCurrency(npsResult.maturity)}
                      </h2>
                    </div>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between border-b border-slate-100 py-4">
                        <span className="font-medium text-muted-foreground">
                          Total Invested
                        </span>
                        <span className="text-xl font-bold text-secondary">
                          {formatCurrency(npsResult.invested)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between border-b border-slate-100 py-4">
                        <span className="font-medium text-muted-foreground">
                          Interest Earned
                        </span>
                        <span className="text-xl font-bold text-green-600">
                          {formatCurrency(npsResult.interest)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between border-b border-slate-100 py-4">
                        <span className="font-medium text-muted-foreground">
                          Maturity Amount
                        </span>
                        <span className="text-xl font-bold text-primary">
                          {formatCurrency(npsResult.maturity)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between border-b border-slate-100 py-4">
                        <span className="font-medium text-muted-foreground">
                          Max Lumpsum (60%)
                        </span>
                        <span className="text-xl font-bold text-secondary">
                          {formatCurrency(npsResult.maturity * 0.6)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between border-b border-slate-100 py-4">
                        <span className="font-medium text-muted-foreground">
                          Min Annuity (40%)
                        </span>
                        <span className="text-xl font-bold text-secondary">
                          {formatCurrency(npsResult.maturity * 0.4)}
                        </span>
                      </div>
                      <div className="mt-4 space-y-2 rounded-2xl bg-gradient-to-br from-cyan-600 to-cyan-700 p-6 text-white shadow-lg">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-cyan-100">
                            Est. Monthly Pension
                          </span>
                          <span className="text-2xl font-black">
                            {formatCurrency(
                              (npsResult.maturity * 0.4 * 0.06) / 12
                            )}
                            *
                          </span>
                        </div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-cyan-200">
                          *Assuming 6% annuity rate on 40% corpus
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ProductLayout>
  );
};
